<!-- components/ImageGallery.vue -->
<template>
  <div class="gallery">
    <div class="gallery__header sticky top-0 flex items-center justify-between gap-10 pb-6 pt-4 -mt-4 px-4 -mx-4 z-10">
      <div class="flex items-center gap-6">
        <DefaultMainLogo class="main-logo" />
        <NuxtLink v-if="title" :to="detailUrl" class="gallery__title font-medium">
          {{ title }}
        </NuxtLink>
      </div>
      <div class="flex items-center gap-6">
        <span class="flex items-center">
          <span>{{ imageIndex }}</span>
          /
          <span>{{ images.length }}</span>
        </span>
        <button
          @click="toggleOverview"
          class="gallery__overview-btn flex items-center gap-1.5 group"
        >
            <span class="hidden sm:inline group-hover:underline font-semibold whitespace-nowrap">
              {{ $t('gallery.overview') }}
            </span>
            <svg class="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3.75 4.5L4.5 3.75H10.5L11.25 4.5V10.5L10.5 11.25H4.5L3.75 10.5V4.5ZM5.25 5.25V9.75H9.75V5.25H5.25ZM12.75 4.5L13.5 3.75H19.5L20.25 4.5V10.5L19.5 11.25H13.5L12.75 10.5V4.5ZM14.25 5.25V9.75H18.75V5.25H14.25ZM3.75 13.5L4.5 12.75H10.5L11.25 13.5V19.5L10.5 20.25H4.5L3.75 19.5V13.5ZM5.25 14.25V18.75H9.75V14.25H5.25ZM12.75 13.5L13.5 12.75H19.5L20.25 13.5V19.5L19.5 20.25H13.5L12.75 19.5V13.5ZM14.25 14.25V18.75H18.75V14.25H14.25Z"></path>
            </svg>
        </button>
        <NuxtLink :to="detailUrl" class="gallery__close-btn">
          <span class="hidden sm:inline font-semibold">Zavřít</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </NuxtLink>
      </div>
    </div>

    <!-- Main Carousel -->
    <div
      v-if="!isOverviewOpen"
      class="mx-0 group"
      id="carousel"
    >
      <div class="hidden lg:block relative">
        <UCarousel
          ref="carouselRef"
          :items="images"
          :ui="{
            container: 'gap-4',
            indicators: {
              wrapper: 'absolute top-[590px] bottom-auto',
              active: 'bg-gray-400 dark:bg-gray-400 mix-blend-plus-darker',
              inactive: 'bg-gray-200 dark:bg-gray-200 mix-blend-plus-darker',
            }
          }"
          arrows
          :indicators="showGalleryIndicators"
        >
          <template #default="{ item }">
            <div class="gallery__slider grid grid-cols-1 gap-4 content-start" draggable="false">
              <div>
                <nuxt-img
                  :src="item.urlWithProtocol"
                  :alt="item.caption"
                  placehoder
                  class="ao__aside h-[580px]"
                />
              </div>
              <div
                class="text-center flex flex-wrap mx-auto"
                :class="{'mt-4': showGalleryIndicators}"
                draggable="false"
              >
                <div v-if="item?.caption || item?.source" class="image-container__caption">
                  <span v-if="item?.caption" class="image-container__title">{{ item?.caption }}</span>
                  <span v-if="item?.caption && item?.source" class="image-container__divider" />
                  <span v-if="item?.source" class="image-container__source">{{ $t('artobjects.source') }}: {{ item?.source }}</span>
                </div>
              </div>
            </div>
          </template>

          <template #next="{ onClick, disabled }">
            <button
              @click="onClick"
              class="next-btn hidden lg:block bg-black/30 p-2.5 pl-3 rounded-full absolute top-72 right-6 transform -translate-y-1/2"
              :class="{'lg:hidden': disabled}"
            >
              <svg class="w-6 h-6 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M16,21 C15.744,21 15.488,20.902 15.293,20.707 L7.293,12.707 C6.902,12.316 6.902,11.684 7.293,11.293 L15.293,3.293 C15.684,2.902 16.316,2.902 16.707,3.293 C17.098,3.684 17.098,4.316 16.707,4.707 L9.414,12 L16.707,19.293 C17.098,19.684 17.098,20.316 16.707,20.707 C16.512,20.902 16.256,21 16,21" transform="matrix(-1 0 0 1 24 0)"/>
              </svg>
            </button>
          </template>

          <template #prev="{ onClick, disabled }">
            <button
              @click="onClick"
              class="prev-btn hidden lg:block bg-black/30 p-2.5 pr-3 rounded-full absolute top-72 left-6 transform -translate-y-1/2"
              :class="{'lg:hidden': disabled}"
            >
              <svg class="w-6 h-6 fill-white transform rotate-180" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M16,21 C15.744,21 15.488,20.902 15.293,20.707 L7.293,12.707 C6.902,12.316 6.902,11.684 7.293,11.293 L15.293,3.293 C15.684,2.902 16.316,2.902 16.707,3.293 C17.098,3.684 17.098,4.316 16.707,4.707 L9.414,12 L16.707,19.293 C17.098,19.684 17.098,20.316 16.707,20.707 C16.512,20.902 16.256,21 16,21" transform="matrix(-1 0 0 1 24 0)"/>
              </svg>
            </button>
          </template>
        </UCarousel>
        <button
          class="gallery__img-btn absolute right-2 sm:right-4 top-4 bg-black/50 p-2 rounded-full block transition-all group-hover:opacity-90 z-30"
          @click="openModal()"
        >
          <svg class="w-6 h-6 fill-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M3 4C3 3.44772 3.44772 3 4 3H8C8.55228 3 9 3.44772 9 4C9 4.55228 8.55228 5 8 5H6.41421L9.70711 8.29289C10.0976 8.68342 10.0976 9.31658 9.70711 9.70711C9.31658 10.0976 8.68342 10.0976 8.29289 9.70711L5 6.41421V8C5 8.55228 4.55228 9 4 9C3.44772 9 3 8.55228 3 8V4ZM16 3H20C20.5523 3 21 3.44772 21 4V8C21 8.55228 20.5523 9 20 9C19.4477 9 19 8.55228 19 8V6.41421L15.7071 9.70711C15.3166 10.0976 14.6834 10.0976 14.2929 9.70711C13.9024 9.31658 13.9024 8.68342 14.2929 8.29289L17.5858 5H16C15.4477 5 15 4.55228 15 4C15 3.44772 15.4477 3 16 3ZM9.70711 14.2929C10.0976 14.6834 10.0976 15.3166 9.70711 15.7071L6.41421 19H8C8.55228 19 9 19.4477 9 20C9 20.5523 8.55228 21 8 21H4C3.44772 21 3 20.5523 3 20V16C3 15.4477 3.44772 15 4 15C4.55228 15 5 15.4477 5 16V17.5858L8.29289 14.2929C8.68342 13.9024 9.31658 13.9024 9.70711 14.2929ZM14.2929 14.2929C14.6834 13.9024 15.3166 13.9024 15.7071 14.2929L19 17.5858V16C19 15.4477 19.4477 15 20 15C20.5523 15 21 15.4477 21 16V20C21 20.5523 20.5523 21 20 21H16C15.4477 21 15 20.5523 15 20C15 19.4477 15.4477 19 16 19H17.5858L14.2929 15.7071C13.9024 15.3166 13.9024 14.6834 14.2929 14.2929Z"></path>
          </svg>
        </button>
      </div>

      <!-- Mobile image feed -->
      <div class="lg:hidden flex flex-col gap-6 cursor-pointer w-full min-w-sm" ref="feedRef">
        <div
          v-for="(image, index) in images"
          :key="index"
          @click="openModal(index)"
        >
          <!-- CNC Ads - above image -->
          <DefaultAdsCncWrapper
            v-if="adPage"
            :position="UniFeAdsPosition.ABOVE_IMAGE"
            :page="adPage"
            :index="index"
          />

          <nuxt-img
            :id="`img-feed-${index}`"
            :src="image.urlWithProtocol || ''"
            :alt="image.caption || ''"
            class="ao__aside"
            placeholder
            v-element-visibility="onFeedImageVisibilityChange(index)"
          />
          <div class="p-2">
            <div v-if="image?.caption || image?.source" class="image-container__caption">
              <span v-if="image?.caption" class="image-container__title">{{ image?.caption }}</span>
              <span v-if="image?.caption && image?.source" class="image-container__divider" />
              <span v-if="image?.source" class="image-container__source">{{ $t('artobjects.source') }}: {{ image?.source }}</span>
            </div>
<!--            <span class="text-md font-medium">{{ image.caption }}</span>-->
          </div>

          <!-- CNC Ads - below image -->
          <DefaultAdsCncWrapper
            v-if="adPage"
            :position="UniFeAdsPosition.BELOW_IMAGE"
            :page="adPage"
            :index="index"
          />
        </div>
      </div>
    </div>

    <!-- Overview -->
    <div
      v-if="isOverviewOpen"
      class="grid grid-cols-2 sm:grid-cols-4 w-full gap-2">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="cursor-pointer w-full min-w-sm"
        @click="showImageFromOverview(index)"
      >
        <nuxt-img
          :src="image.urlWithProtocol || ''"
          :alt="image.caption || ''"
          class="ao__aside"
          placeholder
        />
      </div>
    </div>

    <!-- Fullscreen modal -->
    <div
      v-if="modalOpen"
      class="gallery__preview fixed inset-0 bg-black/90 z-50 grid place-content-center overflow-x-hidden overflow-y-scroll"
      @click.self="closeModal"
    >
      <button
        class="btn btn--trailing absolute top-4 sm:top-12 right-4 z-50 flex gap-0.5 items-center rounded-full border-2 border-white"
        @click="closeModal"
      >
        <span class="font-semibold">{{ $t('gallery.close') }}</span>
        <svg class="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div class="relative">
        <nuxt-img
          :src="images[modalIndex].urlWithProtocol || ''"
          :alt="images[modalIndex].caption || ''"
          class="ao__aside z-40"
          placeholder
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, type PropType, computed} from 'vue'
import type AssetLocal from "~/libs/interfaces/AssetLocal"
// @ts-ignore
import { vElementVisibility } from '@vueuse/components'
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition";
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {useCncAdsWrapper} from "~/composables/useCncAdsWrapper";
import {CncAdsTemplate} from "~/libs/constants/CncAdsTemplate";

const {
  images,
  title,
  detailUrl,
  adPage
} = defineProps({
  images: {type: Array as PropType<AssetLocal[]>, required: false, default: []},
  title: {type: String, required: false},
  detailUrl: {type: String, required: false},
  adPage: { type: String as PropType<CncAdsPage>, required: false },
})

const carouselRef = ref()
const lastCarouselPageCncAds = ref(0)
let lastCarouselPageTimer: any = null

const config = useRuntimeConfig()
const showGalleryIndicators: boolean = !!config.public.showGalleryIndicators

const mainImageIndex = ref(0)
const isOverviewOpen = ref(false)
const modalOpen = ref(false)
const modalIndex = ref(0)

const touchstartX = ref(0)
const touchstartY = ref(0)
const touchendX = ref(0)
const touchendY = ref(0)

let visibleFeedImages: number[] = []

const scrollTo = ref(-1)

const cncPageEvent = () => {
  if (carouselRef.value?.page !== lastCarouselPageCncAds.value) {
    if (lastCarouselPageTimer) {
      clearTimeout(lastCarouselPageTimer)
      lastCarouselPageTimer = null
    }
    lastCarouselPageTimer = setTimeout(() => {
      useCncAdsWrapper().pageEvent(CncAdsTemplate.GALLERY)
      lastCarouselPageCncAds.value = carouselRef.value?.page
    }, 300)
  }
}

const onFeedImageVisibilityChange = (index: number) => (state: any) => {
  if (state) {
    visibleFeedImages.push(index)
  } else {
    visibleFeedImages = visibleFeedImages.filter(item => item !== index)
  }

  visibleFeedImages.sort()
  if (visibleFeedImages.length > 0 && scrollTo.value === -1) {
    mainImageIndex.value = visibleFeedImages.at(0) || 0
  } else {
    // mainImageIndex.value = 0
  }
}

// Default to top is instant
const { scrollToAnchor } = useAnchorScroll({
  toAnchor: {
    scrollOptions: {
      behavior: 'smooth',
      offsetTop: -100,
    }
  },
})


const imageIndex = computed(() => {
  return (mainImageIndex.value + 1)
})

let scrollTimer: any

// Main carousel navigation
const nextMainImage = () => {
  if (carouselRef.value?.page === 0 && mainImageIndex.value < (images.length - 1)) {
    scrollTo.value = mainImageIndex.value + 1
    scrollToFeedImage()
  } else if (carouselRef.value?.page < images.length) {
    carouselRef.value?.next()
    cncPageEvent()
  }
}

const prevMainImage = () => {
  if (carouselRef.value?.page === 0 && mainImageIndex.value > 0) {
    scrollTo.value = mainImageIndex.value - 1
    scrollToFeedImage()
  } else if (carouselRef.value?.page > 1) {
    carouselRef.value?.prev()
    cncPageEvent()
  }
}

const setMainImage = (index: number) => {
  mainImageIndex.value = index
}

const toggleOverview = () => {
  isOverviewOpen.value = !isOverviewOpen.value
}

const showImageFromOverview = async (index: number) => {
  isOverviewOpen.value = false
  scrollTo.value = index
  setTimeout(() => {
    scrollToFeedImage()
  }, 100)
}

const scrollToFeedImage = () => {
  if (scrollTo.value > -1) {
    carouselRef.value?.select(scrollTo.value + 1)
    if (carouselRef.value?.page === 0) {
      scrollToAnchor(`img-feed-${scrollTo.value}`)
      if (scrollTimer) {
        clearTimeout(scrollTimer)
      }
      scrollTimer = setTimeout(() => {
        mainImageIndex.value = scrollTo.value
        scrollTo.value = -1
      }, 400)
    } else {
      scrollTo.value = -1
    }
  }
}

// Modal functions
const openModal = (index?: number) => {
  if (index) {
    modalIndex.value = index
  } else {
    modalIndex.value = carouselRef.value?.page ? Number(carouselRef.value?.page) - 1 : 0
  }
  modalOpen.value = true
}

const closeModal = () => {
  modalOpen.value = false
}


const nextModalImage = () => {
  if (modalIndex.value < images.length - 1) {
    modalIndex.value++
  }
}

const prevModalImage = () => {
  if (modalIndex.value > 0) {
    modalIndex.value--
  }
}

const checkCounter = () => {
  if (carouselRef.value?.page > 0) {
    mainImageIndex.value = carouselRef.value?.page - 1
  } else {
    visibleFeedImages.sort()
    if (visibleFeedImages.length > 0 && scrollTo.value === -1 && !visibleFeedImages.includes(mainImageIndex.value)) {
      mainImageIndex.value = visibleFeedImages.at(0) || 0
    }
  }
}

watch(
  () => carouselRef.value?.page,
  () => {
    if (carouselRef.value?.page > 0) {
      if (carouselRef.value?.page - 1 !== mainImageIndex.value) {
        cncPageEvent()
      }
      mainImageIndex.value = carouselRef.value?.page - 1
    }
  }
)

const setModalIndex = (index: number) => {
  modalIndex.value = index
}

const touchStart = (event: any) => {
  touchstartX.value = event.changedTouches[0].screenX
  touchstartY.value = event.changedTouches[0].screenY
}
const touchEnd = (event: any) => {
  touchendX.value = event.changedTouches[0].screenX
  touchendY.value = event.changedTouches[0].screenY
  handleGesture()
}

const handleGesture = () => {
  if (modalOpen.value) {
    if (touchendX.value < touchstartX.value) {
      prevModalImage()
    }

    if (touchendX.value > touchstartX.value) {
      nextModalImage()
    }
  }
}
//
// Keyboard navigation
onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('touchstart', touchStart)
  window.addEventListener('touchend', touchEnd)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('touchstart', touchStart)
  window.removeEventListener('touchend', touchEnd)
})

const handleKeydown = (e: any) => {
  if (!modalOpen.value) {
    // Main carousel keyboard navigation
    if (e.key === 'ArrowLeft') {
      prevMainImage()
    } else if (e.key === 'ArrowRight') {
      nextMainImage()
    }
    return false
  } else {
    switch (e.key) {
      case 'ArrowLeft':
        prevModalImage()
        break
      case 'ArrowRight':
        nextModalImage()
        break
      case 'Escape':
        closeModal()
        break
    }
  }
}
</script>